import { Component } from '@theme/component';

// Default localStorage key, overridable via `data-storage-key` on the element.
const DEFAULT_STORAGE_KEY = 'ecomyras:recently-viewed';
// Hard cap on how many products we ever keep in localStorage, regardless of
// how many the merchant chooses to *display* (`data-max-items`).
const MAX_STORED_PRODUCTS = 12;

/**
 * @typedef {Object} RecentlyViewedProduct
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 * @property {string|null} image
 * @property {string} price
 */

/**
 * A custom element that tracks and displays recently viewed products entirely
 * client-side (Liquid/the server cannot read localStorage).
 *
 * On every product page:
 * 1. Reads the current product's info from the sibling
 *    `<script type="application/json" data-recently-viewed-current>` tag.
 * 2. Merges it into a deduped, most-recent-first list in localStorage.
 * 3. Renders the stored list (excluding the current product) into the `list` ref.
 * 4. Reveals the section only when there is at least one OTHER product to show,
 *    so a first-ever visit never leaves an empty box or causes layout shift.
 *
 * @typedef {object} RecentlyViewedRefs
 * @property {HTMLUListElement} list
 * @extends {Component<RecentlyViewedRefs>}
 */
class RecentlyViewedProducts extends Component {
  requiredRefs = ['list'];

  connectedCallback() {
    super.connectedCallback();
    this.#trackAndRender();
  }

  /**
   * Reads the current product payload, updates localStorage, and renders the grid.
   */
  #trackAndRender() {
    const current = this.#readCurrentProduct();
    const storageKey = this.dataset.storageKey || DEFAULT_STORAGE_KEY;
    const maxItems = Number.parseInt(this.dataset.maxItems || '6', 10) || 6;

    let stored = this.#readStoredProducts(storageKey);

    if (current?.handle) {
      // De-dupe by handle and push the current product to the front.
      stored = stored.filter((item) => item.handle !== current.handle);
      stored.unshift(current);
      stored = stored.slice(0, MAX_STORED_PRODUCTS);
      this.#writeStoredProducts(storageKey, stored);
    }

    const otherProducts = stored
      .filter((item) => !current || item.handle !== current.handle)
      .slice(0, maxItems);

    this.#render(otherProducts);

    // Only reveal the section on the live storefront once there is something
    // to show. In the theme editor the section is never hidden server-side.
    if (otherProducts.length > 0) {
      this.removeAttribute('hidden');
    }
  }

  /**
   * @returns {RecentlyViewedProduct|null}
   */
  #readCurrentProduct() {
    const script = this.querySelector('script[data-recently-viewed-current]');
    if (!script?.textContent) return null;

    try {
      const data = JSON.parse(script.textContent);
      if (!data?.handle) return null;
      return data;
    } catch (error) {
      console.warn('[recently-viewed] Failed to parse current product payload:', error);
      return null;
    }
  }

  /**
   * @param {string} storageKey
   * @returns {RecentlyViewedProduct[]}
   */
  #readStoredProducts(storageKey) {
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      // Private browsing / storage disabled / corrupted data - fail gracefully.
      console.warn('[recently-viewed] Failed to read localStorage:', error);
      return [];
    }
  }

  /**
   * @param {string} storageKey
   * @param {RecentlyViewedProduct[]} products
   */
  #writeStoredProducts(storageKey, products) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(products));
    } catch (error) {
      // Storage full or unavailable - the section still renders for this pageview.
      console.warn('[recently-viewed] Failed to write localStorage:', error);
    }
  }

  /**
   * @param {RecentlyViewedProduct[]} products
   */
  #render(products) {
    const { list } = this.refs;

    list.innerHTML = products
      .map((product) => {
        const title = this.#escape(product.title);
        const url = this.#escape(product.url);
        const price = this.#escape(product.price);
        const image = product.image
          ? `<img class="recently-viewed__image" src="${this.#escape(product.image)}" alt="${title}" loading="lazy" width="400" height="400">`
          : '';

        return `
          <li class="recently-viewed__item resource-list__item">
            <a class="recently-viewed__link" href="${url}">
              <span class="recently-viewed__image-wrapper">${image}</span>
              <span class="recently-viewed__title">${title}</span>
              <span class="recently-viewed__price">${price}</span>
            </a>
          </li>
        `;
      })
      .join('');
  }

  /**
   * Minimal HTML-escaping helper for inserting untrusted strings via innerHTML.
   * @param {string|null|undefined} value
   * @returns {string}
   */
  #escape(value) {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
